import { useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import RefreshOutlined from '@mui/icons-material/RefreshOutlined';
import { useQuery } from '@tanstack/react-query';

import { comoErroDaApi } from '../../shared/api/erros';
import { formatarValorEmReais } from '../../shared/dominio/moeda';
import { EstadoDeCarregamento } from '../../shared/ui/EstadoDeCarregamento';
import { EstadoDeErro } from '../../shared/ui/EstadoDeErro';
import {
  buscarItensDoContrato,
  confirmarDePara,
  confirmarDeParaEmLote,
  lerCatalogoDoIw,
  opcoesDaFilaDePara,
} from './api/parametrizacao';
import {
  A_VINCULAR,
  O_QUE_FAZER,
  ROTULOS_DA_SITUACAO,
  type ItemDoContrato,
  type LinhaDaComparacao,
  type SituacaoDaComparacao,
} from './api/tipos';
import { competenciaDe } from '../../shared/dominio/datas';

type Filtro = 'a_resolver' | 'a_vincular' | 'vinculados' | 'todos' | SituacaoDaComparacao;

const TAMANHO_DA_PAGINA = 50;

/** A linha pelo código do IW (ou pelo item, na linha só do contrato): não muda quando a linha é vinculada. */
function chaveDa(linha: LinhaDaComparacao): string {
  return linha.codigoIw ?? `contrato:${linha.itemContratualId ?? ''}`;
}

function aVincular(linha: LinhaDaComparacao): boolean {
  return !linha.vinculado && linha.codigoIw !== null && linha.itemContratualId !== null;
}

/** Mesmo código e mesmo preço dos dois lados, ainda sem vínculo: o que dá para vincular de uma vez. */
function iguaisAVincular(linhas: readonly LinhaDaComparacao[]): LinhaDaComparacao[] {
  return linhas.filter((l) => aVincular(l) && l.situacao === 'igual');
}

/** Vincular de uma vez os iguais; as demais linhas seguem uma a uma. */
function VincularIguais({
  linhas,
  aoVincular,
}: {
  linhas: readonly LinhaDaComparacao[];
  aoVincular: (vinculadas: readonly LinhaDaComparacao[]) => void;
}): React.JSX.Element | null {
  const [emCurso, definirEmCurso] = useState(false);
  const [aviso, definirAviso] = useState<{ ok: boolean; texto: string } | null>(null);
  const iguais = iguaisAVincular(linhas);

  if (iguais.length === 0 && aviso === null) return null;

  return (
    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
      {iguais.length > 0 ? (
        <Button
          variant="contained"
          size="small"
          disabled={emCurso}
          onClick={() => {
            definirEmCurso(true);
            definirAviso(null);
            confirmarDeParaEmLote(
              iguais.map((l) => ({ itemContratualId: l.itemContratualId ?? '', codigoIw: l.codigoIw ?? '' })),
            )
              .then((gravados) => {
                aoVincular(iguais);
                definirAviso({ ok: true, texto: `${String(gravados)} vínculo(s) gravado(s).` });
              })
              .catch((causa: unknown) => {
                definirAviso({ ok: false, texto: comoErroDaApi(causa).message });
              })
              .finally(() => {
                definirEmCurso(false);
              });
          }}
        >
          {iguais.length === 1 ? 'Vincular o igual' : `Vincular os ${String(iguais.length)} iguais`}
        </Button>
      ) : null}
      {iguais.length > 0 ? (
        <Typography variant="body2" color="text.secondary">
          Mesmo código e mesmo preço no contrato e no IW.
        </Typography>
      ) : null}
      {aviso !== null ? (
        <Typography
          variant="body2"
          color={aviso.ok ? 'success.main' : 'error.main'}
          role={aviso.ok ? 'status' : 'alert'}
        >
          {aviso.texto}
        </Typography>
      ) : null}
    </Stack>
  );
}

function noFiltro(linha: LinhaDaComparacao, filtro: Filtro): boolean {
  switch (filtro) {
    case 'todos':
      return true;
    case 'a_resolver':
      return linha.situacao !== 'igual' || aVincular(linha);
    case 'a_vincular':
      return aVincular(linha);
    case 'vinculados':
      return linha.vinculado;
    default:
      return linha.situacao === filtro;
  }
}

/** O que precisa de gente primeiro: preço errado e sem preço travam a cobrança; o resto é cobertura. */
function urgencia(linha: LinhaDaComparacao): number {
  if (linha.situacao === 'preco_diverge') return 0;
  if (linha.situacao === 'sem_preco_no_iw') return 1;
  if (aVincular(linha)) return 2;
  if (linha.situacao === 'so_no_contrato') return 3;
  if (linha.situacao === 'so_no_iw') return 4;
  return 5;
}

const COR_DA_SITUACAO: Record<SituacaoDaComparacao, 'success' | 'warning' | 'default'> = {
  igual: 'success',
  preco_diverge: 'warning',
  sem_preco_no_iw: 'warning',
  so_no_iw: 'default',
  so_no_contrato: 'default',
};

/** Escolher o item do contrato para um código que só o IW tem (vínculo manual). */
function EscolherItem({
  contratoId,
  competencia,
  aoEscolher,
}: {
  contratoId: string;
  competencia: string;
  aoEscolher: (item: ItemDoContrato) => void;
}): React.JSX.Element {
  const [busca, definirBusca] = useState('');
  const consulta = useQuery({
    queryKey: ['parametrizacao', 'itens-do-contrato', contratoId, competencia, busca],
    queryFn: () => buscarItensDoContrato({ contratoId, competencia, busca }),
    enabled: busca.trim().length >= 2,
  });

  return (
    <Autocomplete<ItemDoContrato>
      size="small"
      sx={{ minWidth: 260 }}
      options={consulta.data ?? []}
      loading={consulta.isFetching}
      filterOptions={(opcoes) => opcoes}
      getOptionLabel={(item) => `${item.codigo} · ${item.descricao}`}
      noOptionsText={busca.trim().length < 2 ? 'Digite parte do código ou da descrição' : 'Nenhum item do contrato'}
      onInputChange={(_, valor) => {
        definirBusca(valor);
      }}
      onChange={(_, item) => {
        if (item !== null) aoEscolher(item);
      }}
      renderInput={(parametros) => <TextField {...parametros} label="Item do contrato" />}
    />
  );
}

function AcaoDaLinha({
  linha,
  contratoId,
  competencia,
  aoVincular,
}: {
  linha: LinhaDaComparacao;
  contratoId: string;
  competencia: string;
  aoVincular: (item: { id: string; codigo: string | null; descricao: string | null; valor: number | null }) => void;
}): React.JSX.Element | null {
  const [emCurso, definirEmCurso] = useState(false);
  const [erro, definirErro] = useState<string | null>(null);
  const [escolhendo, definirEscolhendo] = useState(false);

  const vincular = (item: {
    id: string;
    codigo: string | null;
    descricao: string | null;
    valor: number | null;
  }): void => {
    if (linha.codigoIw === null) return;
    definirEmCurso(true);
    definirErro(null);
    confirmarDePara(item.id, linha.codigoIw)
      .then(() => {
        aoVincular(item);
      })
      .catch((causa: unknown) => {
        definirErro(comoErroDaApi(causa).message);
      })
      .finally(() => {
        definirEmCurso(false);
      });
  };

  if (linha.vinculado) {
    return <Chip size="small" color="success" label="Vinculado" />;
  }

  return (
    <Stack spacing={0.5} alignItems="flex-start">
      {aVincular(linha) && linha.itemContratualId !== null ? (
        <Button
          size="small"
          variant="contained"
          disabled={emCurso}
          onClick={() => {
            vincular({
              id: linha.itemContratualId ?? '',
              codigo: linha.codigoNoContrato,
              descricao: linha.descricaoNoContrato,
              valor: linha.valorNoContrato,
            });
          }}
        >
          Vincular
        </Button>
      ) : null}
      {linha.situacao === 'so_no_iw' ? (
        escolhendo ? (
          <EscolherItem
            contratoId={contratoId}
            competencia={competencia}
            aoEscolher={(item) => {
              vincular({ id: item.id, codigo: item.codigo, descricao: item.descricao, valor: item.valor });
            }}
          />
        ) : (
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              definirEscolhendo(true);
            }}
          >
            Ligar a um item do contrato
          </Button>
        )
      ) : null}
      {erro !== null ? (
        <Typography variant="caption" color="error.main" role="alert">
          {erro}
        </Typography>
      ) : null}
    </Stack>
  );
}

/**
 * A comparação contrato × IW (`TP-071`, T11): a tabela de preços e o de-para
 * do contrato no IW, lado a lado com os itens do contrato, uma linha por item,
 * com a situação, o que fazer e o vínculo. Nada do IW é guardado além do
 * resultado (`D7` do TP-051).
 */

/** O resultado de uma leitura: remonta a cada leitura nova (`key`), e o vínculo feito aqui muda a linha na hora. */
function ResultadoDaComparacao({
  linhasIniciais,
  contratoId,
  competencia,
  semCoberturaPorCodigo,
}: {
  linhasIniciais: readonly LinhaDaComparacao[];
  contratoId: string;
  competencia: string;
  semCoberturaPorCodigo: ReadonlyMap<string, number>;
}): React.JSX.Element {
  const [linhas, definirLinhas] = useState<LinhaDaComparacao[]>(() => [...linhasIniciais]);
  const [filtro, definirFiltro] = useState<Filtro>('a_resolver');
  const [mostrando, definirMostrando] = useState(TAMANHO_DA_PAGINA);
  // O que a pessoa acabou de vincular fica na tela, marcado, ate ela trocar o filtro: a linha nao some no clique.
  const [vinculadasAgora, definirVinculadasAgora] = useState<ReadonlySet<string>>(new Set());

  const contar = (f: Filtro): number => linhas.filter((l) => noFiltro(l, f)).length;

  // A situação mais urgente primeiro; dentro dela, o que mais apareceu sem cobertura na conferência.
  const visiveis = linhas
    .filter((l) => noFiltro(l, filtro) || vinculadasAgora.has(chaveDa(l)))
    .sort(
      (a, b) =>
        // A linha recém-vinculada fica no lugar em que estava (o grupo "a vincular"), em vez de descer para o fim.
        (vinculadasAgora.has(chaveDa(a)) ? 2 : urgencia(a)) - (vinculadasAgora.has(chaveDa(b)) ? 2 : urgencia(b)) ||
        (semCoberturaPorCodigo.get(b.codigoIw ?? '') ?? 0) - (semCoberturaPorCodigo.get(a.codigoIw ?? '') ?? 0),
    );
  const situacoesVisiveis = [...new Set(visiveis.map((l) => l.situacao))].sort(
    (a, b) => Object.keys(ROTULOS_DA_SITUACAO).indexOf(a) - Object.keys(ROTULOS_DA_SITUACAO).indexOf(b),
  );

  const filtros: { valor: Filtro; rotulo: string }[] = [
    { valor: 'a_resolver', rotulo: 'A resolver' },
    { valor: 'preco_diverge', rotulo: ROTULOS_DA_SITUACAO.preco_diverge },
    { valor: 'sem_preco_no_iw', rotulo: ROTULOS_DA_SITUACAO.sem_preco_no_iw },
    { valor: 'a_vincular', rotulo: 'A vincular' },
    { valor: 'so_no_iw', rotulo: ROTULOS_DA_SITUACAO.so_no_iw },
    { valor: 'so_no_contrato', rotulo: ROTULOS_DA_SITUACAO.so_no_contrato },
    { valor: 'vinculados', rotulo: 'Vinculados' },
    { valor: 'todos', rotulo: 'Todos' },
  ];

  return (
    <>
      <VincularIguais
        linhas={linhas}
        aoVincular={(vinculadas) => {
          const chaves = new Set(vinculadas.map(chaveDa));
          definirVinculadasAgora((atuais) => new Set([...atuais, ...chaves]));
          definirLinhas((atuais) => atuais.map((l) => (chaves.has(chaveDa(l)) ? { ...l, vinculado: true } : l)));
        }}
      />

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap role="group" aria-label="Filtrar por situação">
        {filtros.map((f) => (
          <Chip
            key={f.valor}
            label={`${f.rotulo} · ${String(contar(f.valor))}`}
            color={filtro === f.valor ? 'primary' : 'default'}
            variant={filtro === f.valor ? 'filled' : 'outlined'}
            onClick={() => {
              definirFiltro(f.valor);
              definirMostrando(TAMANHO_DA_PAGINA);
              definirVinculadasAgora(new Set());
            }}
          />
        ))}
      </Stack>

      {situacoesVisiveis.some((sit) => sit !== 'igual') || visiveis.some(aVincular) ? (
        <Alert severity="info" role="note" icon={false}>
          <Stack spacing={0.5}>
            {situacoesVisiveis
              .filter((sit) => sit !== 'igual')
              .map((sit) => (
                <Typography key={sit} variant="body2">
                  <b>{ROTULOS_DA_SITUACAO[sit]}:</b> {O_QUE_FAZER[sit]}
                </Typography>
              ))}
            {visiveis.some(aVincular) ? (
              <Typography variant="body2">
                <b>A vincular:</b> {A_VINCULAR}
              </Typography>
            ) : null}
          </Stack>
        </Alert>
      ) : null}

      {visiveis.length === 0 ? (
        <Alert severity="success" role="status">
          Nada nesta situação.
        </Alert>
      ) : (
        <Table size="small" aria-label="Itens do contrato e do IW">
          <TableHead>
            <TableRow>
              <TableCell>No contrato</TableCell>
              <TableCell>No IW</TableCell>
              <TableCell>Situação</TableCell>
              <TableCell>Vínculo</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visiveis.slice(0, mostrando).map((linha) => {
              const semCobertura = semCoberturaPorCodigo.get(linha.codigoIw ?? '') ?? 0;
              return (
                <TableRow key={chaveDa(linha)}>
                  <TableCell>
                    {linha.codigoNoContrato !== null && linha.descricaoNoContrato !== null ? (
                      <>
                        <Typography variant="body2">
                          <b>{linha.codigoNoContrato}</b> {linha.descricaoNoContrato}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {linha.valorNoContrato !== null ? formatarValorEmReais(linha.valorNoContrato) : 'sem preço'}
                          {linha.unidadeNoContrato !== null ? ` · ${linha.unidadeNoContrato}` : ''}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {linha.codigoIw !== null ? (
                      <>
                        <Typography variant="body2">
                          <b>{linha.codigoIw}</b> {linha.descricaoIw}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {linha.valorIw !== null ? formatarValorEmReais(linha.valorIw) : 'sem preço no IW'}
                          {semCobertura > 0 ? ` · em ${String(semCobertura)} conferência(s) sem cobertura` : ''}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={COR_DA_SITUACAO[linha.situacao]}
                      label={ROTULOS_DA_SITUACAO[linha.situacao]}
                    />
                  </TableCell>
                  <TableCell>
                    <AcaoDaLinha
                      linha={linha}
                      contratoId={contratoId}
                      competencia={competencia}
                      aoVincular={(item) => {
                        // O vínculo vale na hora: a linha vira "vinculada" ao item escolhido, sem ler o IW de novo.
                        definirVinculadasAgora((atuais) => new Set(atuais).add(chaveDa(linha)));
                        // O item escolhido deixa de ser "só no contrato": a linha dele sai, em vez de ficar duas vezes.
                        definirLinhas((atuais) =>
                          atuais
                            .filter(
                              (l) =>
                                !(l.situacao === 'so_no_contrato' && l.itemContratualId === item.id && l !== linha),
                            )
                            .map((l) =>
                              l === linha
                                ? {
                                    ...l,
                                    vinculado: true,
                                    itemContratualId: item.id,
                                    codigoNoContrato: item.codigo,
                                    descricaoNoContrato: item.descricao,
                                    valorNoContrato: item.valor,
                                    situacao:
                                      l.valorIw === null
                                        ? 'sem_preco_no_iw'
                                        : item.valor !== null && Math.abs(item.valor - l.valorIw) > 0.005
                                          ? 'preco_diverge'
                                          : 'igual',
                                  }
                                : l,
                            ),
                        );
                      }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {visiveis.length > mostrando ? (
        <Button
          onClick={() => {
            definirMostrando((n) => n + TAMANHO_DA_PAGINA);
          }}
        >
          Mostrar mais ({String(visiveis.length - mostrando)})
        </Button>
      ) : null}
    </>
  );
}

export function ComparacaoContratoIw({ contratoId }: { contratoId: string }): React.JSX.Element {
  const [competencia, definirCompetencia] = useState(competenciaDe);
  const [rodada, definirRodada] = useState(0);
  // Só a competência em que a pessoa clicou "Atualizar" passa por cima da cache; trocar de mês depois volta à cache.
  const [competenciaAtualizada, definirCompetenciaAtualizada] = useState<string | null>(null);
  const fila = useQuery(opcoesDaFilaDePara(contratoId));

  const semCoberturaPorCodigo = useMemo(
    () => new Map((fila.data ?? []).map((item) => [item.codigoIw ?? '', item.vezesSemCobertura])),
    [fila.data],
  );

  // Lê sozinha ao abrir e ao trocar a competência; "Atualizar" lê de novo. A mesma tabela é a mesma avaliação (D11).
  const leitura = useQuery({
    queryKey: ['parametrizacao', 'contrato-x-iw', contratoId, competencia, rodada],
    queryFn: () => lerCatalogoDoIw({ contratoId, competencia, atualizar: competenciaAtualizada === competencia }),
    enabled: /^\d{4}-\d{2}$/.test(competencia),
    retry: false,
    staleTime: Infinity,
  });

  return (
    <Stack spacing={1.5} component="section" aria-label="Contrato × IW">
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
        <TextField
          label="Competência"
          type="month"
          size="small"
          value={competencia}
          onChange={(evento) => {
            definirCompetencia(evento.target.value);
          }}
          slotProps={{ inputLabel: { shrink: true } }}
          required
        />
        <Button
          variant="text"
          startIcon={<RefreshOutlined />}
          disabled={leitura.isFetching}
          onClick={() => {
            definirCompetenciaAtualizada(competencia);
            definirRodada((anterior) => anterior + 1);
          }}
        >
          Atualizar
        </Button>
      </Stack>

      {leitura.isPending && leitura.fetchStatus !== 'idle' ? <EstadoDeCarregamento linhas={4} /> : null}

      {leitura.isError ? (
        <EstadoDeErro
          erro={leitura.error}
          contexto="Não foi possível ler a tabela do IW"
          aoReprocessar={() => {
            definirCompetenciaAtualizada(competencia);
            definirRodada((anterior) => anterior + 1);
          }}
        />
      ) : null}

      {leitura.isSuccess ? (
        <ResultadoDaComparacao
          key={`${competencia}-${String(rodada)}-${leitura.data.reconciliacao.avaliacaoId}`}
          linhasIniciais={leitura.data.reconciliacao.linhas}
          contratoId={contratoId}
          competencia={competencia}
          semCoberturaPorCodigo={semCoberturaPorCodigo}
        />
      ) : null}
    </Stack>
  );
}
